import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Instagram, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FooterSection = () => {
  const { t, language } = useLanguage();
  const [legalTexts, setLegalTexts] = useState({
    impressum: '',
    datenschutz: '',
    streitbeilegung: '',
  });
  const [showLegalModal, setShowLegalModal] = useState(null);
  
  useEffect(() => {
    const fetchLegalTexts = async () => {
      try {
        const response = await axios.get(`${API}/legal-texts`);
        if (response.data) {
          setLegalTexts(response.data);
        }
      } catch (error) {
        console.log('Using default legal texts');
      }
    };
    fetchLegalTexts();
  }, []);
  
  // Lock body scroll when modal is open
  useEffect(() => {
    if (showLegalModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showLegalModal]);
  
  const legalLinks = [
    { key: 'impressum', label: language === 'de' ? 'Impressum' : 'Imprint' },
    { key: 'datenschutz', label: language === 'de' ? 'Datenschutz' : 'Privacy Policy' },
    { key: 'streitbeilegung', label: language === 'de' ? 'Streitbeilegung' : 'Dispute Resolution' },
  ];
  
  return (
    <footer 
      id="contact"
      className="relative bg-[#030303] py-24 md:py-32 border-t border-white/5"
      data-testid="footer-section"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        {/* Logo */}
        <motion.h3 
          className="font-cinzel text-4xl md:text-5xl tracking-[0.3em] uppercase text-white mb-8"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          ICEBACH
        </motion.h3>
        
        {/* Decorative */}
        <motion.div 
          className="flex items-center justify-center gap-4 mb-10"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.3 }}
        >
          <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#2bfecd]/30" />
          <div className="w-2 h-2 rotate-45 border border-[#2bfecd]/30" />
          <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#2bfecd]/30" />
        </motion.div>
        
        {/* Location */}
        <motion.p 
          className="text-xs tracking-[0.3em] uppercase text-white/30 mb-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.4 }}
        >
          {t('footer.location')}
        </motion.p>
        
        {/* Social Links */}
        <motion.div 
          className="flex flex-col items-center gap-6 mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <div className="flex justify-center gap-10">
            <motion.a 
              href="https://www.instagram.com/icebachsauna/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/30 hover:text-[#2bfecd] transition-all duration-400"
              whileHover={{ scale: 1.2, y: -3 }}
              data-testid="social-instagram"
            >
              <Instagram className="w-5 h-5" strokeWidth={1.5} />
            </motion.a>
            <motion.a 
              href="mailto:hello@icebachsauna.com"
              className="text-white/30 hover:text-[#2bfecd] transition-all duration-400"
              whileHover={{ scale: 1.2, y: -3 }}
              data-testid="social-email"
            >
              <Mail className="w-5 h-5" strokeWidth={1.5} />
            </motion.a>
          </div>
          
          <a 
            href="https://www.instagram.com/icebachsauna/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] tracking-[0.2em] uppercase text-[#2bfecd]/60 hover:text-[#2bfecd] transition-colors"
          >
            {language === 'de' ? 'Folge uns auf Instagram' : 'Follow us on Instagram'}
          </a>
          
          <p className="text-[10px] tracking-[0.15em] text-white/30">
            hello@icebachsauna.com
          </p>
        </motion.div>
        
        {/* Legal Links */}
        <motion.div 
          className="flex justify-center gap-6 mb-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.6 }}
        >
          {legalLinks.map((link) => (
            <button
              key={link.key}
              onClick={() => setShowLegalModal(link.key)}
              className="text-[9px] tracking-[0.15em] uppercase text-white/20 hover:text-[#2bfecd]/60 transition-colors"
            >
              {link.label}
            </button>
          ))}
        </motion.div>
        
        {/* Copyright */}
        <motion.p 
          className="text-[9px] tracking-[0.25em] uppercase text-white/20"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.7 }}
        >
          © {new Date().getFullYear()} ICEBACH SAUNA. {t('footer.rights')}
        </motion.p>
      </div>
      
      {/* Legal Modal */}
      {showLegalModal && (
        <motion.div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => e.target === e.currentTarget && setShowLegalModal(null)}
        >
          <div className="w-full max-w-3xl mx-auto px-6 max-h-[80vh] overflow-y-auto">
            <div className="bg-[#0a0a0a] border border-white/10 p-8 md:p-12">
              <div className="flex justify-between items-start mb-8">
                <h3 className="font-cinzel text-2xl tracking-[0.1em] uppercase text-white">
                  {legalLinks.find(l => l.key === showLegalModal)?.label}
                </h3>
                <button
                  onClick={() => setShowLegalModal(null)}
                  className="text-white/30 hover:text-[#2bfecd] transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <div className="prose prose-invert prose-sm max-w-none">
                {legalTexts[showLegalModal] ? (
                  <div 
                    className="text-sm text-white/60 leading-relaxed whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{ __html: legalTexts[showLegalModal].replace(/\n/g, '<br/>') }}
                  />
                ) : (
                  <p className="text-sm text-white/40 italic">
                    {language === 'de' 
                      ? 'Inhalt wird über das Admin-Panel hinzugefügt.' 
                      : 'Content to be added via Admin Panel.'}
                  </p>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </footer>
  );
};

export default FooterSection;
